import { useMemo } from 'react';
import { TeContainer, TeContainerSlim, TeLabel, TeDate } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { endOfDay, startOfDay, max, min } from 'date-fns';
import { TeDateChangeEvent } from '@topcon/tuicl';

import { Filter, FilterIDs } from '../../../providers/filter/filter.types';
import { useFilters } from '../hooks';
import FilterContainer from '../FilterContainer';
import { RequestToJoinBody } from '../../../api/generated/pap';

interface Props {
  requests: RequestToJoinBody[];
}

function RequestFilters({ requests }: Props) {
  const { t } = useTranslation('users');
  const filterID = FilterIDs.RequestFilters;
  const filterData = useFilters(filterID);
  const { filtersCopy, handleChange } = filterData;

  const [startDate, endDate] = ['requestedFrom', 'requestedTo']
    .map((field) => filtersCopy?.find(({ name }) => name === field)?.value as string | undefined)
    .map((d) => (d ? new Date(d) : undefined));
  const today = new Date();

  const minCreatedAt = useMemo(() => {
    if (!requests.length) return;
    return startOfDay(min(requests.map(({ createdAt }) => new Date(createdAt))));
  }, [requests]);

  const maxCreatedAt = useMemo(() => {
    if (!requests.length) return;
    return endOfDay(max(requests.map(({ createdAt }) => new Date(createdAt))));
  }, [requests]);

  const minStart = minCreatedAt;
  const maxStart = endDate || maxCreatedAt || today;
  const minEnd = startDate || minCreatedAt;
  const maxEnd = maxCreatedAt || today;

  const onStartChange = (e: TeDateChangeEvent) => {
    const d = e.date;
    handleChange(
      new Filter(
        filterID,
        'requestedFrom',
        t('requestedFrom'),
        d ? startOfDay(d).toISOString() : '',
        true,
        'date'
      )
    );
  };

  const onEndChange = (e: TeDateChangeEvent) => {
    const d = e.date;
    handleChange(
      new Filter(
        filterID,
        'requestedTo',
        t('requestedTo'),
        d ? endOfDay(d).toISOString() : '',
        true,
        'date'
      )
    );
  };

  return (
    <FilterContainer
      filterData={filterData}
      title={t('filters.title')}
      description={t('filters.description')}
    >
      <TeContainer padding={['l', 'm']} gap="l" column grow>
        <TeContainerSlim gap="xs" column fill>
          <TeLabel text={t('filters.dateRequested')} />
          <TeContainerSlim gap="xs" fill>
            <TeDate
              date={startDate}
              minDate={minStart}
              maxDate={maxStart}
              techange={onStartChange}
              size="s"
              variant="compact"
            />
            <TeDate
              date={endDate}
              minDate={minEnd}
              maxDate={maxEnd}
              techange={onEndChange}
              size="s"
              variant="compact"
            />
          </TeContainerSlim>
        </TeContainerSlim>
      </TeContainer>
    </FilterContainer>
  );
}

export default RequestFilters;
